import cx from 'classnames';
import {useSelector} from 'react-redux';
import {eventDatesHaveSameDay} from '~/common/utils/time';
import {AddToCalendar} from '~/ViewInvitation/common/AddToCalendar/AddToCalendar';
import {Label} from '~/ViewInvitation/common/Label';
import {LocationBlock} from '~/ViewInvitation/routes/Landing/LocationBlock';
import {
  selectEventEnd,
  selectEventStart,
  selectEventTimezone,
  selectLocationName,
  selectMeetingUrl,
  selectStreetAddress,
  selectTitle,
} from '~/ViewInvitation/selectors/eventDetails';
import {isPreview} from '~/ViewInvitation/utils';
import {
  formatEventDateForView,
  formatEventTimeForView,
  formatEventTimespanForView,
  shouldShowYears,
} from '~/ViewInvitation/utils/date';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './TitleModule.sass';

interface TitleModuleProps {
  isCreateEditPreview?: boolean;
}

// TODO: let's rename this to EventDetails -- we need to update the pandemic keys as well
// TODO: since they already container an 'event_details', some cleanup required
export const TitleModule = ({isCreateEditPreview = false}: TitleModuleProps) => {
  const title = useSelector(selectTitle);
  const start = useSelector(selectEventStart);
  const end = useSelector(selectEventEnd);
  const timezone = useSelector(selectEventTimezone);
  const locationName = useSelector(selectLocationName);
  const streetAddress = useSelector(selectStreetAddress);
  const meetingUrl = useSelector(selectMeetingUrl);
  const shouldShowLocationBlock = Boolean(locationName || streetAddress || meetingUrl);
  const preview = isCreateEditPreview || isPreview();
  const startEndHaveSameDay = eventDatesHaveSameDay(start, end);
  const showStartDateOnly = (start && end && startEndHaveSameDay) || (start && !end);
  const showYear = shouldShowYears(start, end);
  const isLargishAndUp = useIsLargishAndUp();

  return (
    <div className="title-module__container">
      <Typography
        as="h2"
        variant={isLargishAndUp ? 'header3' : 'header4'}
        data-qa-id="event-title"
        classNames="view-invitation__event-title"
        color={title || !preview ? 'charcoal' : 'pebble'}
      >
        {title || (preview ? 'Your event title' : '')}
      </Typography>
      <div
        className={cx('title-module__dates', {'title-module__dates--no-end': showStartDateOnly})}
      >
        {!start ? (
          <div className="title-module__date">
            <Label value="Date" data-qa-id="start" />
            <Typography variant="paragraph2">–</Typography>
          </div>
          // biome-ignore lint/style/noNestedTernary: this is simple enough
        ) : showStartDateOnly ? (
          <>
            <div className="title-module__date">
              <Label value="Date" data-qa-id="start" />
              <Typography variant="paragraph2">
                {formatEventDateForView(start, timezone, showYear)}
              </Typography>
              <Typography variant="paragraph2">
                {end
                  ? formatEventTimespanForView(start, end, timezone)
                  : formatEventTimeForView(start, timezone)}
              </Typography>
            </div>
            <div className="title-module__add-to-calendar">
              <AddToCalendar />
            </div>
          </>
        ) : (
          <>
            <div className="title-module__start-date">
              <Label value="Start Date" data-qa-id="start" />
              <Typography variant="paragraph2">
                {formatEventDateForView(start, timezone, showYear)}
              </Typography>
              <Typography variant="paragraph2">
                {formatEventTimeForView(start, timezone)}
              </Typography>
            </div>
            <div className="title-module__end-date">
              <Label value="End Date" data-qa-id="end" />
              <Typography variant="paragraph2">
                {formatEventDateForView(end, timezone, showYear)}
              </Typography>
              <Typography variant="paragraph2">{formatEventTimeForView(end, timezone)}</Typography>
            </div>
            <div className="title-module__add-to-calendar">
              <AddToCalendar />
            </div>
          </>
        )}
      </div>
      {shouldShowLocationBlock && <LocationBlock className="title-module__location" />}
    </div>
  );
};
